import { useMemo, useEffect, useCallback, useRef, useState } from "react"
import PropTypes from "prop-types"
import { isEqual } from "lodash"
import MetricsSelector from "components/custom-metrics-selector"
import { useMetricsSelectorAnalytics } from "components/custom-metrics-selector/hooks/use-metrics-selector-analytics"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import { getCurrentTemplateMetrics } from "components/custom-metrics-selector/helpers/template-metrics"
import { MODULE_TYPES } from "connected-components/dashboard/modules/types"
import { EMPTY_ARRAY } from "./helpers"

export default function MetricsSelectorWrapper({
  templateMetadata,
  selectedMetrics,
  availableMetrics,
  setSelectedMetrics,
  initialViewName,
  initialSelectedMetrics,
  duplicateSourceTemplateId,
}) {
  const assetViewContext = useAssetViewData()
  const {
    setView,
    view,
    deleteTemplate,
    saveTemplate,
    setShowMetricsSelector,
    showMetricsSelector,
    modelId,
    clearDuplicateState,
    getCurrentViewId,
  } = assetViewContext
  const analytics = useMetricsSelectorAnalytics(assetViewContext, MODULE_TYPES.ASSET_VIEW)

  const currentMetrics = useMemo(() => {
    if (!templateMetadata || !templateMetadata.config.metrics) {
      return []
    }
    return getCurrentTemplateMetrics(templateMetadata.config.metrics, modelId)
  }, [templateMetadata, modelId])

  useEffect(() => {
    // Avoid setting selectedMetrics if they are equivalent to the current metrics (prevents infinite re-renders)
    if (!isEqual(selectedMetrics, currentMetrics)) {
      setSelectedMetrics(currentMetrics)
    }
  }, [currentMetrics, selectedMetrics, setSelectedMetrics])

  const handleCloseWithoutRestore = useCallback(() => {
    if (showMetricsSelector) {
      analytics.trackClosed(templateMetadata)
    }
    setShowMetricsSelector(false, { restoreSourceView: false })
  }, [analytics, showMetricsSelector, setShowMetricsSelector, templateMetadata])

  const handleToggle = useCallback(() => {
    if (showMetricsSelector) {
      analytics.trackClosed(templateMetadata)
    } else {
      analytics.trackOpened(templateMetadata)
    }
    setShowMetricsSelector(!showMetricsSelector, { restoreSourceView: showMetricsSelector })
  }, [analytics, showMetricsSelector, setShowMetricsSelector, templateMetadata])

  const isSavingTemplateRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleSaveMetricsTemplate = useCallback(async (template) => {
    if (isSavingTemplateRef.current) return
    isSavingTemplateRef.current = true
    setIsSaving(true)

    const metricsCount = Object.values(template.metrics || {}).filter(Boolean).length

    let savedTemplate
    try {
      savedTemplate = await saveTemplate({ id: templateMetadata?.id, type: "asset-view", ...template })
    } catch {
      return
    } finally {
      isSavingTemplateRef.current = false
      setIsSaving(false)
    }

    if (templateMetadata?.id) {
      analytics.trackViewUpdated(templateMetadata.id, template.name, metricsCount)
    } else {
      analytics.trackViewSaved(template.name, metricsCount)
    }

    if (savedTemplate) {
      clearDuplicateState()
      if (view !== savedTemplate.id) {
        setView(savedTemplate.id)
      }
    }
  }, [analytics, saveTemplate, setView, clearDuplicateState, templateMetadata, view])

  const isDeletingTemplateRef = useRef(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDeleteCustomTemplate = useCallback(async (template) => {
    if (isDeletingTemplateRef.current) return
    isDeletingTemplateRef.current = true
    setIsDeleting(true)
    try {
      await deleteTemplate(template)
    } catch {
      return
    } finally {
      isDeletingTemplateRef.current = false
      setIsDeleting(false)
    }
    analytics.trackViewDeleted(template.id, template.name)
    if (template.id === getCurrentViewId()) {
      setView("exposure")
    }
  }, [analytics, deleteTemplate, setView, getCurrentViewId])

  const duplicateDataKey = initialSelectedMetrics ? (duplicateSourceTemplateId ?? "built-in") : ""

  return <MetricsSelector
    // This is needed for re-calculating the state within the metrics selector
    key={showMetricsSelector ? `${templateMetadata?.id || "new"}-${showMetricsSelector}-${modelId}-${duplicateDataKey}` : "closed"}
    modelId={modelId}
    isOpen={showMetricsSelector}
    toggle={handleToggle}
    closeWithoutRestore={handleCloseWithoutRestore}
    setSelectedMetrics={(metrics) => setSelectedMetrics(metrics)}
    includeMetricTypes
    defaultSelectedMetrics={initialSelectedMetrics ?? EMPTY_ARRAY}
    availableMetrics={availableMetrics}
    saveCustomTemplate={handleSaveMetricsTemplate}
    deleteCustomTemplate={handleDeleteCustomTemplate}
    customTemplate={templateMetadata}
    analytics={analytics}
    initialViewName={initialViewName}
    isSaving={isSaving}
    isDeleting={isDeleting}
  />
}

MetricsSelectorWrapper.propTypes = {
  templateMetadata: PropTypes.object.isRequired,
  availableMetrics: PropTypes.array.isRequired,
  setSelectedMetrics: PropTypes.func.isRequired,
  selectedMetrics: PropTypes.array.isRequired,
  initialViewName: PropTypes.string,
  initialSelectedMetrics: PropTypes.arrayOf(PropTypes.string),
  duplicateSourceTemplateId: PropTypes.string,
}
